import { useQuery } from 'react-query';
import { generatePath } from 'react-router-dom';
import { oktaGet, oktaPost } from '../oktaRequestWrapper';
import { ApiRoutes } from '../apiRoutes';
import { FilterInfo } from './SearchInvoices';
import {
  Carrier,
  CarrierNoaDetail,
  CarrierNoaDetailResponse,
  CarrierSearchResponse,
  NoaDocument,
  NoaDocumentResponse,
  NoaJobStatus,
} from '@/app/features/DebtorPortal/NOACenter/noaCenter.types';

const NOA_API_VERSION = '1.0';

const buildCarrierSearchBody = (term: string, size: number) => {
  const filter: FilterInfo | null =
    term.length > 2
      ? {
          condition: 'or',
          filter: [
            { field: 'carrierName', condition: 'contains', value: term },
            { field: 'mcNumber', condition: 'contains', value: term },
            { field: 'dotNumber', condition: 'contains', value: term },
          ],
        }
      : null;

  return {
    returnFullResultSet: false,
    pagination: { size, page: 1 },
    filter,
    sort: [{ field: 'carrierName', order: 'asc' }],
  };
};

const isDownloadableNoaJob = (status: NoaJobStatus) =>
  status === NoaJobStatus.Complete || status === NoaJobStatus.Presenting;

const toNoaDocument = (
  doc: NoaDocumentResponse,
  carrier: Carrier | null | undefined,
): NoaDocument => ({
  documentId: doc.documentId,
  documentType: doc.type,
  issuedDate: carrier?.issuedDate ?? doc.dateCreated,
  effectiveDate: carrier?.effectiveDate,
  isLatest: true,
  isDownloadable: isDownloadableNoaJob(doc.status),
});

const toCarrierNoaDetail = (
  response: CarrierNoaDetailResponse,
): CarrierNoaDetail => ({
  carrier: response.carrier,
  documents: (response.documents ?? []).map((doc) =>
    toNoaDocument(doc, response.carrier),
  ),
});

export const useSearchCarriers = (searchTerm: string, size = 25) => {
  const term = searchTerm.trim();
  return useQuery({
    queryKey: ['noa-carrier-search', term, size],
    keepPreviousData: true,
    queryFn: async (): Promise<Carrier[]> => {
      const response = await oktaPost<CarrierSearchResponse>(
        ApiRoutes.NoaCarrierSearch,
        buildCarrierSearchBody(term, size),
        { headers: { 'api-version': NOA_API_VERSION } },
      );
      return response.data?.results ?? [];
    },
  });
};

export const useCarrierNoaDetail = (carrierId: string | null) =>
  useQuery({
    queryKey: ['noa-carrier-detail', carrierId],
    enabled: !!carrierId,
    queryFn: async (): Promise<CarrierNoaDetail | null> => {
      if (!carrierId) return null;

      const route = generatePath(ApiRoutes.NoaCarrierDetail, { carrierId });
      const response = await oktaGet<CarrierNoaDetailResponse>(route, {
        headers: { 'api-version': NOA_API_VERSION },
      });
      return response.data ? toCarrierNoaDetail(response.data) : null;
    },
  });

export const getNoaDocumentDownloadUrl = async (
  documentId: string,
): Promise<string> => {
  const route = generatePath(ApiRoutes.NoaDocumentDownload, { documentId });
  const response = await oktaGet<string>(route, {
    headers: { 'api-version': NOA_API_VERSION },
  });
  return response.data;
};
